import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { ReportFilters } from '../../types/Report';
import {
  comparisonDashboardService,
  ComparisonWidget,
  GroupByDimension,
} from '../../services/comparisonDashboardService';
import { reportFiltersToComparisonFilters } from '../../utils/reportFiltersComparison';
import { EXECUTIVE_BREAKDOWN_DEFAULT_GROUP_BY } from './comparisonBreakdownConstants';
import ComparisonBreakdownGrid from './ComparisonBreakdownGrid';

export interface ExecutiveComparisonBreakdownPanelProps {
  reportFilters: ReportFilters;
  /** When false, show a prompt instead of fetching (matches executive “run report” flow). */
  enabled: boolean;
}

/**
 * Comparison-style grouped metrics for the executive dashboard (same API as Comparison Dashboard).
 */
const ExecutiveComparisonBreakdownPanel: React.FC<ExecutiveComparisonBreakdownPanelProps> = ({
  reportFilters,
  enabled,
}) => {
  const [groupBy, setGroupBy] = useState<GroupByDimension>(EXECUTIVE_BREAKDOWN_DEFAULT_GROUP_BY);
  const [widgets, setWidgets] = useState<ComparisonWidget[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const comparisonFilters = useMemo(
    () => reportFiltersToComparisonFilters(reportFilters),
    [reportFilters]
  );

  const loadData = useCallback(
    async (f: ReportFilters, gb: GroupByDimension, offset = 0, append = false) => {
      const filters = reportFiltersToComparisonFilters(f);
      if (!filters.start_date || !filters.end_date) {
        setWidgets([]);
        setHasMore(false);
        return;
      }

      setError(null);
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const data = await comparisonDashboardService.getComparisonData(filters, gb, offset);
        if (append) {
          setWidgets((prev) => {
            const seen = new Set(prev.map((w) => w.group_key));
            return [...prev, ...data.widgets.filter((w) => !seen.has(w.group_key))];
          });
        } else {
          setWidgets(data.widgets);
        }
        setHasMore(data.has_more);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load breakdown';
        setError(message);
        if (!append) {
          setWidgets([]);
          setHasMore(false);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!enabled) {
      setWidgets([]);
      setHasMore(false);
      return;
    }

    let cancelled = false;
    const filters = reportFiltersToComparisonFilters(reportFilters);
    if (!filters.start_date || !filters.end_date) {
      setWidgets([]);
      setHasMore(false);
      return;
    }

    setError(null);
    setLoading(true);

    void (async () => {
      try {
        const data = await comparisonDashboardService.getComparisonData(filters, groupBy, 0);
        if (cancelled) return;
        setWidgets(data.widgets);
        setHasMore(data.has_more);
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load breakdown';
        setError(message);
        setWidgets([]);
        setHasMore(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, groupBy, reportFilters]);

  if (!enabled) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '2.5rem 1rem',
          color: 'var(--report-text-muted)',
          fontSize: '0.95rem',
        }}
      >
        Run the report to load the dimensional breakdown.
      </div>
    );
  }

  return (
    <div style={{ marginTop: '0.5rem' }}>
      {error && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--report-error-bg)',
            color: 'var(--report-error-text)',
            borderRadius: '6px',
            border: '1px solid var(--report-error-border)',
            fontSize: '0.9rem',
          }}
        >
          {error}
        </div>
      )}
      <ComparisonBreakdownGrid
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        widgets={widgets}
        loading={loading}
        comparisonFilters={comparisonFilters}
        hasMore={hasMore}
        onLoadMore={() => loadData(reportFilters, groupBy, widgets.length, true)}
        loadingMore={loadingMore}
      />
    </div>
  );
};

export default ExecutiveComparisonBreakdownPanel;
